import { InputAdornment } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import type {
  WelcomeBonusCampaignBonus,
  WelcomeBonusGameWeightingCategory,
  WelcomeBonusGameWeightingContributions,
} from '../../../../../@types/api';
import { WELCOME_BONUS_GAME_WEIGHTING_OPTIONS } from '../../../../../constants';
import { CAMPAIGN_REWARD_TYPE } from '../../../../../constants/enums';
import type { WelcomeBonusCreateFormData } from '../../../../../schema';
import FormFieldStack from '../../../../atoms/FormFieldStack';
import { StyledSwitch } from '../../../../atoms/Switch';
import FormNumberInput from '../../../../molecules/FormNumberInput';
import {
  RequiredCasinoWageringFields,
  createGameWeightingContributionRows,
  gameWeightingPayloadKeyMap,
  toGameWeightingContributionsPayload,
} from '../WageringFields';
import {
  WelcomeBonusFormSection,
  WelcomeBonusReviewField,
  WelcomeBonusReviewGrid,
} from '../WelcomeBonusFormPrimitives';

export const getDefaultGameWeightingContributions = (): WelcomeBonusCreateFormData['gameWeightingContributions'] => [
  ...createGameWeightingContributionRows(0),
];

export const getCasinoProductDefaults = () =>
  ({
    rewardType: CAMPAIGN_REWARD_TYPE.REAL_MONEY,
    minSelections: null,
    minOddsPerSelection: null,
    maxBetLimit: null,
    gameWeightingContributions: getDefaultGameWeightingContributions(),
    incrementalUnlocksEnabled: false,
    unlockBatchPercentage: null,
  }) satisfies Partial<WelcomeBonusCreateFormData>;

export const buildCasinoProductPayload = (data: WelcomeBonusCreateFormData) => ({
  rewardType: CAMPAIGN_REWARD_TYPE.REAL_MONEY,
  maxBetLimit: data.maxBetLimit,
  gameWeightingContributions: toGameWeightingContributionsPayload(data.gameWeightingContributions),
  incrementalUnlocksEnabled: data.incrementalUnlocksEnabled,
  unlockBatchPercentage: data.incrementalUnlocksEnabled ? data.unlockBatchPercentage : null,
});

export const CasinoCreateFields = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext<WelcomeBonusCreateFormData>();
  const incrementalUnlocksEnabled = useWatch({ control, name: 'incrementalUnlocksEnabled' });

  return (
    <>
      <WelcomeBonusFormSection title='Casino settings'>
        <RequiredCasinoWageringFields />
      </WelcomeBonusFormSection>
      <WelcomeBonusFormSection title='Incremental unlocks'>
        <Controller
          name='incrementalUnlocksEnabled'
          control={control}
          render={({ field: { value, onChange, name } }) => (
            <FormFieldStack label='Incremental Unlocks'>
              <StyledSwitch
                checked={value}
                onChange={(_event, checked) => onChange(checked)}
                name={name}
                slotProps={{ input: { 'aria-label': 'Incremental Unlocks' } }}
              />
            </FormFieldStack>
          )}
        />
        {incrementalUnlocksEnabled && (
          <FormNumberInput
            control={control}
            name='unlockBatchPercentage'
            label='Unlock Batch Percentage'
            error={errors.unlockBatchPercentage}
            allowDecimals={false}
            slotProps={{ input: { endAdornment: <InputAdornment position='end'>%</InputAdornment> } }}
          />
        )}
      </WelcomeBonusFormSection>
    </>
  );
};

const getGameWeightingValue = (
  contributions: WelcomeBonusGameWeightingContributions | null | undefined,
  category: WelcomeBonusGameWeightingCategory
) => {
  const key = gameWeightingPayloadKeyMap[category];
  const value = contributions?.[key];

  return value == null ? null : `${value}%`;
};

type CasinoReviewFieldsProps = {
  campaignBonus?: WelcomeBonusCampaignBonus | null;
  currencySymbol?: string;
};

const formatEnabledState = (enabled: boolean | null | undefined) => {
  if (enabled == null) return null;

  return enabled ? 'Yes' : 'No';
};

export const CasinoReviewFields = ({ campaignBonus, currencySymbol = '' }: CasinoReviewFieldsProps) => (
  <WelcomeBonusFormSection title='Casino settings'>
    <WelcomeBonusReviewGrid>
      <WelcomeBonusReviewField
        label='Max Bet Limit'
        value={campaignBonus?.maxBetLimit == null ? null : `${campaignBonus.maxBetLimit}${currencySymbol}`}
      />
      {WELCOME_BONUS_GAME_WEIGHTING_OPTIONS.map((option) => (
        <WelcomeBonusReviewField
          key={option.id}
          label={`${option.name} contribution`}
          value={getGameWeightingValue(campaignBonus?.gameWeightingContributions, option.id)}
        />
      ))}
      <WelcomeBonusReviewField
        label='Incremental Unlocks'
        value={formatEnabledState(campaignBonus?.incrementalUnlocksEnabled)}
      />
      {campaignBonus?.incrementalUnlocksEnabled && (
        <WelcomeBonusReviewField
          label='Unlock Batch Percentage'
          value={campaignBonus.unlockBatchPercentage == null ? null : `${campaignBonus.unlockBatchPercentage}%`}
        />
      )}
    </WelcomeBonusReviewGrid>
  </WelcomeBonusFormSection>
);
